import { useState } from 'react'
import {Briefcase, MapPin, Clock3, ChevronDown, Heart, GraduationCap, TrendingUp, Users2} from 'lucide-react'
import Layout from '@/components/Layout'
import Container from '@/components/ui/Container'
import SectionHeading from '@/components/ui/SectionHeading'
import PageHero from '@/components/PageHero'
import JobApplicationForm from '@/components/JobApplicationForm'
import { jobs } from '@/data/jobs'

const cultureValues = [
  { id: 'cv1', icon: Heart, title: 'Cuidado com pessoas', description: 'Valorizamos o bem-estar e o crescimento de cada colaborador.' },
  { id: 'cv2', icon: GraduationCap, title: 'Aprendizado contínuo', description: 'Incentivamos certificações técnicas e desenvolvimento constante.' },
  { id: 'cv3', icon: TrendingUp, title: 'Crescimento real', description: 'Planos de carreira claros e oportunidades de evolução interna.' },
  { id: 'cv4', icon: Users2, title: 'Colaboração', description: 'Times multidisciplinares que trabalham em conjunto por resultado.' },
]

function Careers() {
  const [openJobId, setOpenJobId] = useState<string | null>(null)

  return (
    <Layout>
      <PageHero
        eyebrow="Carreiras"
        title="Construa sua carreira em tecnologia e segurança"
        description="Fazemos parte de um time apaixonado por proteger o que importa. Conheça nossas vagas abertas e a cultura que nos move."
      />

      <section className="py-20 sm:py-24">
        <Container>
          <SectionHeading
            eyebrow="Nossa cultura"
            title="Por que trabalhar com a gente"
            align="center"
          />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {cultureValues.map(value => {
              const Icon = value.icon
              return (
                <div key={value.id} className="rounded-2xl border border-slate-100 p-6 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-sm font-semibold text-slate-900">{value.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">{value.description}</p>
                </div>
              )
            })}
          </div>
        </Container>
      </section>

      <section className="bg-slate-50 py-20 sm:py-24">
        <Container>
          <SectionHeading
            eyebrow="Vagas abertas"
            title="Oportunidades disponíveis"
            description={`${jobs.length} vagas abertas em diferentes áreas e localidades.`}
          />

          <div className="mt-12 space-y-4">
            {jobs.map(job => {
              const isOpen = openJobId === job.id
              return (
                <div key={job.id} className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenJobId(isOpen ? null : job.id)}
                    aria-expanded={isOpen}
                    className="flex w-full flex-col gap-3 p-6 text-left sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-cyan-700">
                        {job.department}
                      </span>
                      <h3 className="mt-2 text-lg font-semibold text-slate-900">{job.title}</h3>
                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {job.location}</span>
                        <span className="flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" aria-hidden="true" /> {job.type}</span>
                      </div>
                    </div>
                    <span className="flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
                      {isOpen ? 'Fechar detalhes' : 'Ver detalhes'}
                      <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </span>
                  </button>

                  {isOpen ? (
                    <div className="border-t border-slate-100 p-6 pt-5">
                      <p className="text-sm leading-relaxed text-slate-600">{job.description}</p>
                      <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-slate-500">Requisitos</p>
                      <ul className="mt-2 space-y-1.5">
                        {job.requirements.map(req => (
                          <li key={req} className="flex items-start gap-2 text-sm text-slate-600">
                            <Briefcase className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-600" aria-hidden="true" />
                            {req}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6">
                        <JobApplicationForm jobTitle={job.title} />
                      </div>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </Container>
      </section>
    </Layout>
  )
}

export default Careers
